/**
 * Phase 0 — demo fixtures only.
 * Reuses approved mock SSOT; never invents prices.
 */

import {
  INITIAL_APPLIED_SEARCH,
  MOCK_FILTER_DESTINATIONS,
  MOCK_PACKAGES,
  formatFx,
  formatIls,
  getAgentNetIls,
  getAgentNetOriginal,
  getAgentNetTotalIls,
  getAgentNetTotalOriginal,
  getPackageCurrency,
} from "../packagesResultsMockup/mockPackagesData";
import {
  MOCK_FILTERS,
  MOCK_OFFERS,
  MOCK_SEARCH as FE2_MOCK_SEARCH,
} from "../flightEngineV2/mock/flightEngineMockData";

/** Destinations known to at least one fixture set. */
export const UNIFIED_DESTINATIONS = [
  {
    id: "budapest",
    label: "בודפשט",
    countryCode: "HU",
    countryLabel: "הונגריה",
    hubwayzNameHint: "budapest",
    hubwayzLocationId: 912,
    packageDestinationId: "budapest",
    flightIata: "BUD",
    originDefault: "TLV",
  },
  {
    id: "larnaca",
    label: "לרנקה",
    countryCode: "CY",
    countryLabel: "קפריסין",
    hubwayzNameHint: "larnaca",
    packageDestinationId: null,
    flightIata: "LCA",
    originDefault: "TLV",
  },
  {
    id: "dubai",
    label: "דובאי",
    countryCode: "AE",
    countryLabel: "איחוד האמירויות",
    hubwayzNameHint: "dubai",
    hubwayzLocationId: 1789,
    packageDestinationId: "dubai",
    flightIata: "DXB",
    originDefault: "TLV",
  },
  {
    id: "prague",
    label: "פראג",
    countryCode: "CZ",
    countryLabel: "צ׳כיה",
    hubwayzNameHint: "prague",
    packageDestinationId: "prague",
    flightIata: "PRG",
    originDefault: "TLV",
  },
  {
    id: "athens",
    label: "אתונה",
    countryCode: "GR",
    countryLabel: "יוון",
    hubwayzNameHint: "athens",
    packageDestinationId: "athens",
    flightIata: "ATH",
    originDefault: "TLV",
  },
];

export function createDefaultRooms() {
  return [{ adults: 2, childAges: [] }];
}

export function createInitialDraftSearch() {
  // Dubai has reliable Hubwayz live inventory in current sandbox; Budapest can return empty for some dates.
  const dest = UNIFIED_DESTINATIONS.find((d) => d.id === "dubai");
  return {
    origin: "TLV",
    originLabel: "תל אביב",
    destinationId: dest.id,
    destinationLabel: dest.label,
    packageDestinationId: dest.packageDestinationId,
    flightIata: dest.flightIata,
    checkIn: "2026-12-10",
    checkOut: "2026-12-14",
    dateRangeLabel: "10–14 בדצמבר 2026",
    tripType: "roundtrip",
    cabin: "economy",
    rooms: createDefaultRooms(),
  };
}

export function cloneSearch(search) {
  return {
    ...search,
    rooms: (search?.rooms || []).map((r) => ({
      adults: Number(r.adults) || 1,
      childAges: Array.isArray(r.childAges) ? [...r.childAges] : [],
    })),
  };
}

export function summarizeOccupancy(rooms) {
  const list = Array.isArray(rooms) && rooms.length ? rooms : createDefaultRooms();
  let adults = 0;
  let children = 0;
  list.forEach((r) => {
    adults += Math.max(0, Number(r.adults) || 0);
    children += Array.isArray(r.childAges) ? r.childAges.length : 0;
  });
  const roomCount = list.length;
  const parts = [];
  parts.push(adults === 1 ? "מבוגר אחד" : `${adults} מבוגרים`);
  if (children > 0) parts.push(children === 1 ? "ילד אחד" : `${children} ילדים`);
  parts.push(roomCount === 1 ? "חדר אחד" : `${roomCount} חדרים`);
  return {
    adults,
    children,
    infants: 0,
    rooms: roomCount,
    occupancyLabel: parts.join(" · "),
  };
}

/** Project unified search → FE2 search VM shape (display only). */
export function toFe2SearchVm(applied) {
  const occ = summarizeOccupancy(applied.rooms);
  return {
    ...FE2_MOCK_SEARCH,
    origin: applied.origin || "TLV",
    originCity: applied.originLabel || "תל אביב",
    destination: applied.flightIata || "",
    destinationCity: applied.destinationLabel || "",
    tripType: applied.tripType || "roundtrip",
    tripTypeLabel: applied.tripType === "oneway" ? "הלוך" : "הלוך-חזור",
    departDate: applied.checkIn,
    departDateLabel: applied.checkIn
      ? applied.checkIn.split("-").reverse().join("/")
      : "",
    returnDate: applied.checkOut,
    returnDateLabel: applied.checkOut
      ? applied.checkOut.split("-").reverse().join("/")
      : "",
    passengers: {
      adults: occ.adults,
      children: occ.children,
      infants: 0,
    },
    passengersLabel: occ.occupancyLabel,
    cabin: applied.cabin || "economy",
    cabinLabel:
      applied.cabin === "business"
        ? "עסקים"
        : applied.cabin === "premium"
          ? "פרימיום"
          : "תיירות",
  };
}

/** Filter package fixtures that match applied destination — no invented rows. */
export function matchPackageFixtures(applied) {
  const destId = applied?.packageDestinationId;
  if (!destId) {
    return { items: [], matched: false, reason: "no_package_fixture" };
  }
  const items = MOCK_PACKAGES.filter((p) => p.destinationId === destId);
  return {
    items,
    matched: items.length > 0,
    reason: items.length ? null : "no_package_fixture",
  };
}

/** Filter flight fixtures by arrival IATA on outbound leg — no invented rows. */
export function matchFlightFixtures(applied) {
  const iata = String(applied?.flightIata || "")
    .trim()
    .toUpperCase();
  if (!iata) {
    return { items: [], matched: false, reason: "no_flight_fixture", filters: MOCK_FILTERS };
  }
  const items = MOCK_OFFERS.filter((offer) => {
    const arr = String(offer?.outbound?.arrIata || "").toUpperCase();
    return arr === iata;
  });
  return {
    items,
    matched: items.length > 0,
    reason: items.length ? null : "no_flight_fixture",
    filters: {
      ...MOCK_FILTERS,
      resultCount: items.length,
    },
  };
}

export {
  INITIAL_APPLIED_SEARCH,
  MOCK_FILTER_DESTINATIONS,
  MOCK_PACKAGES,
  MOCK_OFFERS,
  MOCK_FILTERS,
  formatFx,
  formatIls,
  getAgentNetIls,
  getAgentNetOriginal,
  getAgentNetTotalIls,
  getAgentNetTotalOriginal,
  getPackageCurrency,
};
